'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';

const TESTIMONIAL_IMAGES_ROW1 = [
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_162e89785-1763296121474.png", imgAlt: "Jennifer Okafor, female homeowner, professional smiling photo" },
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_179ebd6f2-1763294255544.png", imgAlt: "David Chen, male landlord, professional friendly photo" },
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_14723756c-1763300179179.png", imgAlt: "Aisha Patel, female homeowner, professional confident photo" },
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9a2c999-1772209893236.png", imgAlt: "Marcus Webb, male property manager, professional photo" },
];

const TESTIMONIAL_IMAGES_ROW2 = [
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb5d2cb2-1763294526193.png", imgAlt: "Keisha Thompson, female landlord, professional warm photo" },
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_179ebd6f2-1763294255544.png", imgAlt: "Tom Kowalski, male homeowner, casual photo" },
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_1152b30b9-1763301565633.png", imgAlt: "Fatima Al-Hassan, female real estate investor, elegant photo" },
  { img: "https://img.rocket.new/generatedImages/rocket_gen_img_13332bcfc-1772209894406.png", imgAlt: "Ryan Nguyen, male homeowner, relaxed photo" },
];

function TestimonialCard({ quote, name, title, img, imgAlt }: { quote: string; name: string; title: string; img: string; imgAlt: string }) {
  return (
    <div className="w-[380px] shrink-0 glass-panel rounded-2xl p-6 flex flex-col justify-between border border-dispatch-surface hover:border-dispatch-cyan/30 transition-all duration-500 cursor-default">
      <div className="flex gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((s) => (
          <svg key={s} width="12" height="12" viewBox="0 0 24 24" fill="#FFB800">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        ))}
      </div>
      <p className="text-dispatch-muted font-dm-sans text-sm leading-relaxed flex-1">"{quote}"</p>
      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-dispatch-surface">
        <AppImage src={img} alt={imgAlt} width={36} height={36} className="rounded-full object-cover ring-1 ring-dispatch-surface" />
        <div>
          <p className="text-dispatch-text font-manrope font-semibold text-sm">{name}</p>
          <p className="text-dispatch-faint font-mono text-[10px]">{title}</p>
        </div>
      </div>
    </div>
  );
}

export default function TestimonialMarquee() {
  const { language } = useLanguage();
  const t = getTranslations(language).testimonials;

  const row1 = t.row1.map((item, i) => ({ ...item, ...TESTIMONIAL_IMAGES_ROW1[i] }));
  const row2 = t.row2.map((item, i) => ({ ...item, ...TESTIMONIAL_IMAGES_ROW2[i] }));

  const doubled1 = [...row1, ...row1];
  const doubled2 = [...row2, ...row2];

  return (
    <section className="py-24 overflow-hidden border-y border-dispatch-surface/40">
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <h2 className="font-manrope font-extrabold text-dispatch-text text-3xl lg:text-4xl tracking-tight">
          {t.headline1}<br />
          <span className="text-dispatch-cyan">{t.headline2}</span>
        </h2>
      </div>

      <div className="relative">
        <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-dispatch-bg to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-dispatch-bg to-transparent z-10 pointer-events-none" />

        <div className="marquee-track flex flex-col gap-5">
          <div className="overflow-hidden">
            <div className="flex gap-5 marquee-left w-max py-2 px-6">
              {doubled1.map((t, i) => <TestimonialCard key={i} {...t} />)}
            </div>
          </div>
          <div className="overflow-hidden">
            <div className="flex gap-5 marquee-right w-max py-2 px-6">
              {doubled2.map((t, i) => <TestimonialCard key={i} {...t} />)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}